import React, { useState } from 'react';
import { Plus, Camera, Users, Edit3, Trash2, Medal, Star, BadgeCheck, Lock, Unlock } from 'lucide-react';
import { doc, getDoc, collection, setDoc, updateDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { Player, Config } from '../../types';
import { resizeImage } from '../../lib/utils';
import { getCategoryStyles } from '../../lib/categoryStyles';
import { LazyItem } from '../common/LazyItem';

interface AdminJugadoresProps {
  players: Player[];
  userRole: string;
  db: any;
  getCollectionPath: (col: string) => string;
  config: Config;
}

export function AdminJugadores({ players, userRole, db, getCollectionPath, config }: AdminJugadoresProps) {
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [category, setCategory] = useState('6ta');
  const [gender, setGender] = useState('Masculino');
  const [photoUrl, setPhotoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({ achievements: [] });
  const [confirmReset, setConfirmReset] = useState(false);
  const [activeSuspensionId, setActiveSuspensionId] = useState<string | null>(null);
  const [suspensionDuration, setSuspensionDuration] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsLoading(true);
      resizeImage(file, (compressed) => {
        setPhotoUrl(compressed);
        setIsLoading(false);
      });
    }
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      resizeImage(file, (compressed) => {
        setEditForm({ ...editForm, photoUrl: compressed });
      });
    }
  };

  const handleAddPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;
    setIsLoading(true);
    try {
      let finalPhotoUrl = photoUrl;
      if (finalPhotoUrl.startsWith('data:image/')) {
        const imageRef = ref(storage, `avatars/admin_${Date.now()}.jpg`);
        await uploadString(imageRef, finalPhotoUrl, 'data_url');
        finalPhotoUrl = await getDownloadURL(imageRef);
      }

      const playerRef = doc(collection(db, getCollectionPath('players')));
      const pId = playerRef.id;
      await setDoc(playerRef, {
        id: pId,
        name,
        nickname,
        position,
        category,
        gender,
        photoUrl: finalPhotoUrl,
        phone,
        createdAt: Date.now(),
        achievements: [],
      });
      setName('');
      setNickname('');
      setPosition('');
      setPhone('');
      setPhotoUrl('');
    } catch (err) {
      console.error("Error creating player:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const openEditMode = (p: Player) => {
    setEditingPlayerId(editingPlayerId === p.id ? null : p.id);
    setEditForm({
      name: p.name || '',
      nickname: p.nickname || '',
      position: p.position || '',
      category: p.category || '6ta',
      gender: p.gender || 'Masculino',
      photoUrl: p.photoUrl || '',
      phone: p.phone || '',
      points: p.points !== undefined ? p.points : 0,
      achievements: Array.isArray(p.achievements) ? p.achievements : [],
      isOrganizer: p.isOrganizer || false,
      isValidated: p.isValidated || false,
    });
  };

  const saveEdit = async (pId: string) => {
    try {
      if (editForm.isOrganizer) {
        const uDoc = await getDoc(doc(db, 'users', pId));
        if (uDoc.exists()) {
          const userData = uDoc.data();
          if (userData.clubId || (userData.role && userData.role !== 'user' && userData.role !== 'gestor_torneos')) {
            alert('⚠️ Este jugador forma parte del staff de un club. No se puede habilitar como organizador independiente. Debes removerlo del staff del club primero.');
            return;
          }
        }
      }

      let finalPhotoUrl = editForm.photoUrl;
      if (finalPhotoUrl && finalPhotoUrl.startsWith('data:image/')) {
        const imageRef = ref(storage, `avatars/${pId}_${Date.now()}.jpg`);
        await uploadString(imageRef, finalPhotoUrl, 'data_url');
        finalPhotoUrl = await getDownloadURL(imageRef);
      }
      await updateDoc(doc(db, getCollectionPath('players'), pId), {
        ...editForm,
        photoUrl: finalPhotoUrl
      });
      
      try {
        await updateDoc(doc(db, 'users', pId), {
          isValidated: editForm.isValidated
        });
      } catch (e) {
        console.error("Error syncing isValidated to users collection:", e);
      }

      setEditingPlayerId(null);
    } catch (err) {
      console.error("Error saving player edit:", err);
    }
  };

  const handleResetStats = async () => {
    const qMatches = query(collection(db, getCollectionPath('matches')), where('status', '==', 'completed'));
    const snap = await getDocs(qMatches);
    for (const d of snap.docs) {
      await deleteDoc(doc(db, getCollectionPath('matches'), d.id));
    }
    setConfirmReset(false);
  };

  const handleSuspendPlayer = async (playerId: string, duration: string) => {
    if (!duration) return;
    
    let msToAdd = 0;
    let label = '';
    if (duration === '1_day') {
      msToAdd = 24 * 60 * 60 * 1000;
      label = '1 día';
    } else if (duration === '3_days') {
      msToAdd = 3 * 24 * 60 * 60 * 1000;
      label = '3 días';
    } else if (duration === '1_week') {
      msToAdd = 7 * 24 * 60 * 60 * 1000;
      label = '1 semana';
    } else if (duration === '1_month') {
      msToAdd = 30 * 24 * 60 * 60 * 1000;
      label = '1 mes';
    } else if (duration === 'forever') {
      msToAdd = -1;
      label = 'tiempo indefinido';
    }

    const until = msToAdd === -1 ? null : Date.now() + msToAdd;
    const reason = `Inhabilitación de cuenta por ${label}`;

    try {
      await updateDoc(doc(db, getCollectionPath('players'), playerId), {
        isSuspended: true,
        suspendedUntil: until,
        suspensionReason: reason,
      });

      const notifRef = doc(collection(db, getCollectionPath('notifications')));
      await setDoc(notifRef, {
        id: notifRef.id,
        userId: playerId,
        type: 'warning',
        title: '⚠️ Cuenta Inhabilitada',
        message: `Tu cuenta ha sido inhabilitada por ${label}. Si crees que se trata de un error, comunícate con soporte.`,
        read: false,
        createdAt: Date.now(),
      });

      setActiveSuspensionId(null);
      setSuspensionDuration('');
    } catch (err) {
      console.error(err);
      alert('Error al inhabilitar jugador');
    }
  };

  const handleLiftSuspension = async (playerId: string) => {
    try {
      await updateDoc(doc(db, getCollectionPath('players'), playerId), {
        isSuspended: false,
        suspendedUntil: null,
        suspensionReason: '',
      });

      const notifRef = doc(collection(db, getCollectionPath('notifications')));
      await setDoc(notifRef, {
        id: notifRef.id,
        userId: playerId,
        type: 'warning',
        title: '✅ Cuenta Habilitada',
        message: '¡Tu cuenta ha sido reactivada! Ya puedes inscribirte en torneos, reportar partidos y enviar mensajes.',
        read: false,
        createdAt: Date.now(),
      });
    } catch (err) {
      console.error(err);
      alert('Error al habilitar jugador');
    }
  };

  const handleVerifyPlayer = async (id: string) => {
    try {
      await updateDoc(doc(db, getCollectionPath('players'), id), {
        isValidated: true
      });
    } catch (e) {
      console.error(e);
      alert('Error al verificar jugador');
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={handleAddPlayer}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-12 gap-2 bg-black/30 p-3 rounded-2xl border border-white/5 items-end"
      >
        <div className="col-span-2 sm:col-span-3 lg:col-span-3">
          <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Nombre Completo</label>
          <input
            required
            type="text"
            placeholder="Nombre Apellido"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg p-2 text-xs outline-none focus:theme-primary-border text-white"
          />
        </div>

        <div className="col-span-1 sm:col-span-1 lg:col-span-2">
          <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Apodo (Opcional)</label>
          <input
            type="text"
            placeholder="Apodo"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg p-2 text-xs outline-none focus:theme-primary-border text-white"
          />
        </div>

        <div className="col-span-1 sm:col-span-1 lg:col-span-2">
          <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Posición</label>
          <select
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg p-2 text-xs outline-none font-bold text-white"
          >
            <option value="">Posición...</option>
            <option value="Drive">Drive</option>
            <option value="Revés">Revés</option>
          </select>
        </div>

        <div className="col-span-1 sm:col-span-1 lg:col-span-1">
          <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Categoría</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg p-2 text-xs outline-none font-bold text-white"
          >
            {['7ma', '6ta', '5ta', '4ta', '3ra', '2da', '1ra'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-1 sm:col-span-1 lg:col-span-2">
          <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Foto</label>
          <div className="relative flex items-center justify-center bg-black/40 border border-white/10 rounded-lg h-[34px] overflow-hidden group cursor-pointer hover:theme-primary-border">
            <input type="file" accept="image/*" onChange={handleFileChange} className="absolute inset-0 opacity-0 cursor-pointer z-10 text-white placeholder-white/30" />
            {isLoading ? (
              <span className="text-xs theme-primary-text animate-pulse">...</span>
            ) : photoUrl ? (
              <img src={photoUrl} alt="Preview" className="h-full w-full object-cover rounded opacity-50 group-hover:opacity-100" />
            ) : (
              <div className="flex items-center gap-1 opacity-50">
                <Camera size={12} />
                <span className="text-[9px] font-black uppercase">Subir</span>
              </div>
            )}
          </div>
        </div>

        <div className="col-span-2 sm:col-span-1 lg:col-span-2">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full theme-primary-bg text-black font-black rounded-lg h-[34px] flex items-center justify-center transition-all uppercase tracking-wider hover:opacity-85 text-[10px]"
          >
            <Plus size={14} className="mr-0.5" /> Agregar
          </button>
        </div>
      </form>
      <div className="overflow-x-auto bg-black/20 rounded-2xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead className="bg-black/40 opacity-80 uppercase text-[10px] tracking-wider font-black">
            <tr>
              <th className="py-2.5 px-3 rounded-tl-2xl">
                Jugador ({players.filter(p => !p.isGuest).length})
              </th>
              <th className="hidden sm:table-cell py-2.5 px-3">Categoría / Pos.</th>
              <th className="py-2.5 px-3">Puntos</th>
              <th className="py-2.5 px-3 text-right rounded-tr-2xl">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {players.filter(p => !p.isGuest).map((p) => {
              const isSuspendedActive = p.isSuspended && (p.suspendedUntil === null || p.suspendedUntil > Date.now());
              return (
                <React.Fragment key={p.id}>
                  <LazyItem as="tr" estimatedHeight={52} className="hover:bg-white/5 transition-colors">
                    <td className="py-2 px-3 font-bold flex items-center gap-2">
                      {p.photoUrl ? (
                        <img src={p.photoUrl} className="w-8 h-8 rounded-full object-cover" alt="img" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60">
                          <Users size={14} />
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-sm leading-tight">{p.name}</span>
                          {p.isValidated && <BadgeCheck size={14} className="text-emerald-400" />}
                          {isSuspendedActive && (
                            <span className="bg-red-500/15 text-red-400 text-[9px] px-1.5 py-0.5 rounded font-black uppercase tracking-wider border border-red-500/20">
                              🚫 Inhabilitado
                            </span>
                          )}
                        </div>
                        {p.nickname && <span className="text-[10px] theme-primary-text italic opacity-80">"{p.nickname}"</span>}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell py-2 px-3">
                      <div className="flex flex-col items-start gap-0.5">
                        <span className={`hidden sm:inline-block border rounded px-1.5 py-0.2 text-[10px] font-bold ${getCategoryStyles(p.category).badgeBg} ${getCategoryStyles(p.category).text}`}>
                          {p.category}
                        </span>
                        {p.position && <span className="text-[9px] opacity-60 uppercase font-black tracking-wide">{p.position}</span>}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-black theme-primary-text">
                          {parseInt(String(p.points || '0'), 10) || 0} pts
                        </span>
                        {p.winStreak && p.winStreak >= 3 && (
                          <span className="bg-gradient-to-r from-orange-600 to-red-600 text-white text-[8px] px-1 rounded font-bold uppercase tracking-wider animate-pulse">
                            🔥 1.5x
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">
                      {confirmDeleteId === p.id ? (
                        <div className="flex items-center justify-end gap-1.5 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded-lg shrink-0">
                          <span className="text-[10px] font-black text-red-400 mr-1 uppercase">¿Eliminar a {p.name}?</span>
                          <button
                            type="button"
                            onClick={() => deleteDoc(doc(db, getCollectionPath('players'), p.id))}
                            className="text-[9px] bg-red-500 hover:bg-red-600 text-white px-2 py-0.5 rounded font-black uppercase transition-colors"
                          >
                            Sí
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-[9px] bg-white/10 hover:bg-white/20 text-white px-2 py-0.5 rounded font-black uppercase transition-colors"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          {/* 0. WhatsApp */}
                          {p.phone && (
                            <a
                              href={`https://wa.me/${p.phone.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-400 hover:text-green-300 p-1 bg-green-400/10 rounded-lg flex items-center justify-center shrink-0"
                              title={`Contactar por WhatsApp (${p.phone})`}
                            >
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                              </svg>
                            </a>
                          )}

                          {/* 1. Editar */}
                          <button
                            type="button"
                            onClick={() => openEditMode(p)}
                            className="text-white/60 hover:text-white px-2 py-1 bg-white/5 rounded-lg text-xs font-black uppercase tracking-wider border border-white/5"
                            title="Editar"
                          >
                            <Edit3 size={12} />
                          </button>

                          {/* 2. Inhabilitar / Habilitar */}
                          {isSuspendedActive ? (
                            <button
                              type="button"
                              onClick={() => handleLiftSuspension(p.id)}
                              className="bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 px-1 py-[1.5px] rounded text-[8px] font-black uppercase tracking-tight transition-all flex items-center gap-0.5 shrink-0"
                              title="Habilitar jugador"
                            >
                              <Unlock size={8} /> Habilitar
                            </button>
                          ) : (
                            <div className="relative shrink-0">
                              <select
                                value=""
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleSuspendPlayer(p.id, e.target.value);
                                  }
                                }}
                                className="appearance-none bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 px-1 py-[1.5px] rounded text-[8px] font-black uppercase tracking-tight transition-all cursor-pointer text-center outline-none select-none"
                              >
                                <option value="" disabled className="bg-neutral-950 text-red-400 font-bold uppercase text-[8px]">Inhabilitar ▾</option>
                                <option value="1_day" className="bg-neutral-950 text-white font-normal">1 día</option>
                                <option value="3_days" className="bg-neutral-950 text-white font-normal">3 días</option>
                                <option value="1_week" className="bg-neutral-950 text-white font-normal">1 semana</option>
                                <option value="1_month" className="bg-neutral-950 text-white font-normal">1 mes</option>
                                <option value="forever" className="bg-neutral-950 text-white font-normal">Para siempre</option>
                              </select>
                            </div>
                          )}

                          {/* 3. Eliminar */}
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteId(p.id)}
                            className="text-red-400 hover:text-red-300 p-1 bg-red-400/10 rounded-lg"
                            title="Eliminar"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </td>
                  </LazyItem>
                {editingPlayerId === p.id && (
                  <tr className="bg-black/40 border-b-2 theme-primary-border">
                    <td colSpan={4} className="p-2 md:p-3">
                      <div className="max-w-md mx-auto bg-black/80 p-4 rounded-2xl border border-white/10 shadow-2xl space-y-4">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-white/5 pb-2">
                          <span className="text-xs font-black uppercase tracking-wider theme-primary-text">📝 Editar Jugador</span>
                          <button
                            type="button"
                            onClick={() => setEditingPlayerId(null)}
                            className="text-[10px] text-white/40 hover:text-white uppercase font-black"
                          >
                            Cerrar
                          </button>
                        </div>

                        {/* Personal Data Grid */}
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="col-span-2 flex items-center gap-3 bg-white/5 p-2 rounded-xl border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-black/60 overflow-hidden relative flex-shrink-0 cursor-pointer group border border-white/10">
                              {editForm.photoUrl ? (
                                <img
                                  src={editForm.photoUrl}
                                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100"
                                  alt="edit"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center opacity-50">
                                  <Camera size={14} />
                                </div>
                              )}
                              <input type="file" accept="image/*" onChange={handleEditFileChange} className="absolute inset-0 opacity-0 cursor-pointer text-white placeholder-white/30" />
                            </div>
                            <div className="flex-1">
                              <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Nombre Completo</label>
                              <input
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                placeholder="Nombre completo"
                                className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs font-bold outline-none focus:theme-primary-border text-white"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Apodo (Opcional)</label>
                            <input
                              value={editForm.nickname}
                              onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })}
                              placeholder="Apodo"
                              className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs outline-none focus:theme-primary-border text-white"
                            />
                          </div>

                          <div>
                            <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Posición</label>
                            <select
                              value={editForm.position}
                              onChange={(e) => setEditForm({ ...editForm, position: e.target.value })}
                              className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs outline-none focus:theme-primary-border font-bold text-white"
                            >
                              <option value="">Sin Posición</option>
                              <option value="Drive">Drive</option>
                              <option value="Revés">Revés</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Categoría</label>
                            <select
                              value={editForm.category}
                              onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                              className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs outline-none focus:theme-primary-border text-white font-bold"
                            >
                              {['7ma', '6ta', '5ta', '4ta', '3ra', '2da', '1ra'].map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Género</label>
                            <select
                              value={editForm.gender}
                              onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                              className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs outline-none focus:theme-primary-border text-white font-bold"
                            >
                              <option value="Masculino">Masculino</option>
                              <option value="Femenino">Femenino</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">Puntos de Ranking</label>
                            <input
                              type="number"
                              value={editForm.points !== undefined ? editForm.points : 0}
                              onChange={(e) => setEditForm({ ...editForm, points: Number(e.target.value) })}
                              placeholder="Puntos"
                              className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs outline-none focus:theme-primary-border font-bold text-white"
                            />
                          </div>

                          <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <div>
                              <label className="text-[9px] opacity-40 font-bold uppercase block mb-1">WhatsApp</label>
                              <input
                                value={editForm.phone}
                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                placeholder="Teléfono (WhatsApp)"
                                className="w-full bg-black/60 border border-white/10 rounded-lg p-1.5 text-xs outline-none focus:theme-primary-border text-white"
                              />
                            </div>
                            {userRole === 'superadmin' && (
                              <>
                                <div className="flex flex-col items-start justify-center pt-2">
                                  <label className="text-[9px] opacity-60 font-bold uppercase block mb-1 text-theme-primary">Modo Organizador</label>
                                  <label className="flex items-center gap-2 cursor-pointer bg-white/5 px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/10 transition-colors w-full">
                                    <input
                                      type="checkbox"
                                      checked={editForm.isOrganizer}
                                      onChange={(e) => setEditForm({ ...editForm, isOrganizer: e.target.checked })}
                                      className="w-3.5 h-3.5 rounded bg-black/50 border border-white/20 accent-[#eab308]"
                                    />
                                    <span className="text-[10px] font-bold text-white/80">Gestión de Torneos</span>
                                  </label>
                                </div>
                                <div className="flex flex-col items-start justify-center pt-2">
                                  <label className="text-[9px] opacity-60 font-bold uppercase block mb-1 text-emerald-400">Estado de Perfil</label>
                                  <label className="flex items-center gap-2 cursor-pointer bg-emerald-500/10 px-3 py-1.5 rounded-lg border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors w-full">
                                    <input
                                      type="checkbox"
                                      checked={editForm.isValidated}
                                      onChange={(e) => setEditForm({ ...editForm, isValidated: e.target.checked })}
                                      className="w-3.5 h-3.5 rounded bg-black/50 border border-emerald-500/30 accent-emerald-500"
                                    />
                                    <span className="text-[10px] font-bold text-emerald-400">Perfil Verificado</span>
                                  </label>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Medals Block */}
                        {config.feature_medals !== false && (
                          <div className="bg-white/5 p-3 rounded-xl border border-white/5 space-y-2">
                            <span className="text-[9px] opacity-40 font-bold uppercase block">🏅 Logros Destacados (Máximo 3)</span>
                            {editForm.achievements.map((ach: any, idx: number) => (
                              <div
                                key={idx}
                                className="flex gap-1.5 items-center bg-black/40 p-1.5 rounded-lg border border-white/5 text-xs"
                              >
                                <Medal
                                  size={16}
                                  className={
                                    ach.type === 'oro'
                                      ? 'text-yellow-400 drop-shadow-[0_0_3px_rgba(250,204,21,0.5)]'
                                      : ach.type === 'plata'
                                        ? 'text-slate-300'
                                        : 'text-amber-700'
                                  }
                                />
                                <select
                                  value={ach.type}
                                  onChange={(e) => {
                                    const newA = [...editForm.achievements];
                                    newA[idx].type = e.target.value;
                                    setEditForm({ ...editForm, achievements: newA });
                                  }}
                                  className="bg-black/60 border border-white/10 rounded-lg p-1 text-xs outline-none font-bold text-white shrink-0"
                                >
                                  <option value="oro">Oro</option>
                                  <option value="plata">Plata</option>
                                  <option value="bronce">Bronce</option>
                                </select>
                                <input
                                  value={ach.text}
                                  onChange={(e) => {
                                    const newA = [...editForm.achievements];
                                    newA[idx].text = e.target.value;
                                    setEditForm({ ...editForm, achievements: newA });
                                  }}
                                  placeholder="Ej: Campeón Roca Padel Tour"
                                  className="flex-1 min-w-0 bg-black/60 border border-white/10 rounded-lg p-1 text-xs outline-none text-white"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newA = [...editForm.achievements];
                                    newA.splice(idx, 1);
                                    setEditForm({ ...editForm, achievements: newA });
                                  }}
                                  className="text-red-400 hover:text-red-300 bg-red-500/10 p-1 rounded-lg transition-colors shrink-0"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                            {editForm.achievements.length < 3 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setEditForm({
                                    ...editForm,
                                    achievements: [...editForm.achievements, { type: 'oro', text: '' }],
                                  })
                                }
                                className="text-[9px] theme-primary-text hover:opacity-80 bg-white/5 px-2.5 py-1 rounded-md font-bold transition-colors border border-white/10 block w-full text-center uppercase tracking-wide"
                              >
                                + Agregar Medalla
                              </button>
                            )}
                          </div>
                        )}

                        {/* Save Button */}
                        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                          <button
                            type="button"
                            onClick={() => setEditingPlayerId(null)}
                            className="text-xs bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl text-white font-bold uppercase tracking-wider transition-colors"
                          >
                            Cancelar
                          </button>
                          <button
                            type="button"
                            onClick={() => saveEdit(p.id)}
                            className="text-xs theme-primary-bg text-black font-black px-4 py-2 rounded-xl uppercase tracking-wider transition-colors hover:opacity-80"
                          >
                            Guardar Cambios
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          </tbody>
        </table>
      </div>

      {userRole === 'superadmin' && config.feature_stats !== false && (
        <div className="pt-12 mt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center bg-white/5 p-6 rounded-2xl border border-white/5 gap-4">
            <div className="text-center md:text-left">
              <h4 className="text-white/40 font-bold text-sm uppercase tracking-widest">Mantenimiento de Temporada</h4>
              <p className="text-[10px] text-white/20 mt-1">Limpieza de resultados históricos para inicio de nuevo ciclo.</p>
            </div>

            {confirmReset ? (
              <div className="flex items-center gap-2 bg-black/40 p-2 rounded-xl border border-white/10">
                <span className="text-[10px] opacity-60 font-bold px-2 uppercase">¿Confirmar reinicio global?</span>
                <button
                  onClick={handleResetStats}
                  className="bg-white/10 hover:bg-red-500 text-white px-4 py-2 rounded-lg text-xs font-black transition-colors"
                >
                  Confirmar
                </button>
                <button onClick={() => setConfirmReset(false)} className="bg-black/20 text-white px-4 py-2 rounded-lg text-xs font-bold">
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmReset(true)}
                className="text-[10px] font-black opacity-20 hover:opacity-100 hover:text-red-400 transition-all uppercase tracking-widest py-2 px-4 border border-transparent hover:border-red-500/20 rounded-lg"
              >
                Reiniciar Estadísticas
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
